/**
 * SIDEBAR - CHATBOT IMÓVEIS®
 * =================================================================
 * Componente de navegação lateral (sidebar) para dashboard
 *
 * Funcionalidades:
 * - Navegação por grupos (Leads, WhatsApp, Analytics, Premium)
 * - Estados ativos com Wouter
 * - Badges de contador (leads não lidos, etc)
 * - Collapsible (expand/collapse) com persistência
 * - Hover expand em modo colapsado
 * - Tooltips Radix em modo colapsado
 * - ScrollArea para conteúdo longo
 *
 * Stack: React 18 + Tailwind + Radix UI + Wouter + Brand Tokens
 * A11y: WCAG 2.2 AA compliant
 * Data: 2025-11-09
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import {
  Home,
  Users,
  MessageCircle,
  BarChart3,
  FileText,
  Settings,
  Sparkles,
  Globe,
  Shield,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Zap,
  Crown,
  Bot,
  Calendar,
  Layers,
  UserPlus,
} from 'lucide-react';
import { useFirebaseAuth } from '@/contexts/FirebaseAuthContext';

// Radix UI Components
import * as ScrollArea from '@radix-ui/react-scroll-area';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as Collapsible from '@radix-ui/react-collapsible';

/* ========================================
 * TYPES & INTERFACES
 * ======================================== */

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ElementType;
  badge?: number | string;
  badgeColor?: 'success' | 'warning' | 'error' | 'info' | 'premium';
  description?: string;
  premium?: boolean;
  requiresRole?: string[];
}

interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
  collapsible?: boolean;
  requiresRole?: string[];
}

interface SidebarProps {
  /** Forçar estado colapsado (controlado externamente) */
  collapsed?: boolean;
  /** Callback quando estado de colapso muda */
  onCollapseChange?: (collapsed: boolean) => void;
  /** Classe CSS adicional */
  className?: string;
  /** Contadores dinâmicos de badges */
  badges?: {
    leads?: number;
    whatsapp?: number;
    notifications?: number;
  };
}

/* ========================================
 * NAVIGATION DATA
 * ======================================== */

const navigationSections: NavSection[] = [
  {
    id: 'main',
    title: 'Principal',
    items: [
      {
        id: 'dashboard',
        label: 'Centro de Comando',
        path: '/dashboard',
        icon: Home,
        description: 'Visão geral e KPIs',
      },
      {
        id: 'leads',
        label: 'Leads / Agenda',
        path: '/dashboard/leads/agenda',
        icon: Calendar,
        badge: 0, // Será substituído por prop
        badgeColor: 'info',
        description: 'Agenda de clientes',
      },
      {
        id: 'novo-lead',
        label: 'Criar Novo Lead',
        path: '/dashboard/organizador-clientes/novo-lead',
        icon: UserPlus,
        description: 'Adicionar novo cliente',
      },
      {
        id: 'kanban',
        label: 'Kanban',
        path: '/dashboard/kanban',
        icon: Layers,
        description: 'Gestão visual de leads',
      },
      {
        id: 'zeca-sdr',
        label: 'Zeca SDR',
        path: '/dashboard/sdr',
        icon: Bot,
        badge: 'AI',
        badgeColor: 'premium',
        description: 'Assistente SDR com IA',
      },
      {
        id: 'whatsapp',
        label: 'WhatsApp',
        path: '/dashboard/whatsapp-settings',
        icon: MessageCircle,
        badge: 0, // Será substituído por prop
        badgeColor: 'success',
        description: 'WhatsApp Business',
      },
      {
        id: 'analytics',
        label: 'Analytics',
        path: '/dashboard/analytics',
        icon: BarChart3,
        description: 'Relatórios e métricas',
      },
    ],
  },
  {
    id: 'premium',
    title: 'Módulos Premium',
    collapsible: true,
    items: [
      {
        id: 'ai-assistant',
        label: 'IA Premium',
        path: '/dashboard/ai-assistant',
        icon: Sparkles,
        premium: true,
        description: 'Assistente com IA avançada',
      },
      {
        id: 'contracts',
        label: 'Contratos',
        path: '/dashboard/contratos-digitais',
        icon: FileText,
        premium: true,
        description: 'Gestão de contratos',
      },
      {
        id: 'vitrine',
        label: 'Vitrine Online',
        path: '/dashboard/vitrine-online',
        icon: Globe,
        premium: true,
        description: 'Site de imóveis',
      },
    ],
  },
  {
    id: 'system',
    title: 'Sistema',
    collapsible: true,
    items: [
      {
        id: 'settings',
        label: 'Configurações',
        path: '/dashboard/settings',
        icon: Settings,
        description: 'Preferências do sistema',
      },
      // TODO: Implementar página de segurança (admin/owner) - LGPD, 2FA, audit logs
      // {
      //   id: 'security',
      //   label: 'Segurança',
      //   path: '/dashboard/security',
      //   icon: Shield,
      //   description: 'Privacidade e LGPD',
      //   requiresRole: ['admin', 'owner'],
      // },
      // TODO: Avaliar redirecionamento para /help público ou criar versão dashboard
      // {
      //   id: 'help',
      //   label: 'Ajuda',
      //   path: '/dashboard/help',
      //   icon: HelpCircle,
      //   description: 'Central de ajuda',
      // },
    ],
  },
];

/* ========================================
 * COMPONENT
 * ======================================== */

export function Sidebar({
  collapsed: controlledCollapsed,
  onCollapseChange,
  className = '',
  badges = {},
}: SidebarProps) {
  const { user } = useFirebaseAuth();
  const userRole = user?.role || 'agent';

  // Estado de colapso (controlado ou não controlado)
  const [location] = useLocation();
  const [isCollapsedInternal, setIsCollapsedInternal] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('sidebar-collapsed');
    return stored === 'true';
  });

  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : isCollapsedInternal;

  // Estados de seções colapsáveis
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    const stored = localStorage.getItem('sidebar-sections');
    return stored ? JSON.parse(stored) : { main: true, premium: false, system: false };
  });

  // Hover state para expand temporário
  const [isHovering, setIsHovering] = useState(false);

  // Persistir estado de colapso
  useEffect(() => {
    if (controlledCollapsed === undefined) {
      localStorage.setItem('sidebar-collapsed', String(isCollapsedInternal));
    }
  }, [isCollapsedInternal, controlledCollapsed]);

  // Persistir estado de seções
  useEffect(() => {
    localStorage.setItem('sidebar-sections', JSON.stringify(expandedSections));
  }, [expandedSections]);

  // Handler de toggle collapse
  const handleToggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    if (controlledCollapsed === undefined) {
      setIsCollapsedInternal(newCollapsed);
    }
    onCollapseChange?.(newCollapsed);
  };

  // Handler de toggle seção
  const handleToggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Verificar se item está ativo
  const isItemActive = (path: string): boolean => {
    if (path === '/dashboard') {
      return location === '/dashboard' || location === '/dashboard/';
    }
    return location.startsWith(path);
  };

  // Verificar se usuário tem acesso ao item
  const hasAccess = (item: NavItem): boolean => {
    if (!item.requiresRole) return true;
    return item.requiresRole.includes(userRole);
  };

  // Aplicar badges dinâmicos
  const getItemBadge = (item: NavItem): number | string | undefined => {
    if (item.id === 'leads' && badges.leads !== undefined) return badges.leads;
    if (item.id === 'whatsapp' && badges.whatsapp !== undefined) return badges.whatsapp;
    return item.badge;
  };

  // Classes de badge por cor
  const getBadgeClasses = (color?: NavItem['badgeColor']): string => {
    const base =
      'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-bold';
    switch (color) {
      case 'success':
        return `${base} bg-green-500 text-white`;
      case 'warning':
        return `${base} bg-yellow-500 text-gray-900`;
      case 'error':
        return `${base} bg-red-500 text-white`;
      case 'info':
        return `${base} bg-blue-500 text-white`;
      case 'premium':
        return `${base} bg-gradient-to-r from-purple-500 to-pink-500 text-white`;
      default:
        return `${base} bg-gray-500 text-white`;
    }
  };

  // Mostrar sidebar expandida se collapsed mas hovering
  const showExpanded = !isCollapsed || isHovering;

  return (
    <Tooltip.Provider delayDuration={300}>
      <aside
        className={`fixed left-0 top-[64px] bottom-0 z-40
                   bg-white dark:bg-gray-900
                   border-r border-gray-200 dark:border-gray-800
                   transition-all duration-brand-normal ease-brand
                   ${showExpanded ? 'w-sidebar' : 'w-[72px]'}
                   ${className}`}
        onMouseEnter={() => isCollapsed && setIsHovering(true)}
        onMouseLeave={() => isCollapsed && setIsHovering(false)}
        data-testid='dashboard-sidebar'
        role='navigation'
        aria-label='Menu lateral de navegação'
      >
        <ScrollArea.Root className='h-full'>
          <ScrollArea.Viewport className='h-full'>
            <div className='flex flex-col h-full py-4'>
              {/* ========================================
               * HEADER - TOGGLE COLLAPSE
               * ======================================== */}
              <div className='px-3 mb-4'>
                <button
                  onClick={handleToggleCollapse}
                  className='flex items-center justify-center w-full h-10 rounded-brand-md
                             text-[#111827] dark:text-gray-300
                             hover:bg-[#F1F5F9] dark:hover:bg-gray-800
                             focus-brand
                             transition-colors duration-brand-fast'
                  aria-label={isCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
                  title={isCollapsed ? 'Expandir (Ctrl+B)' : 'Colapsar (Ctrl+B)'}
                  data-testid='sidebar-toggle'
                >
                  {isCollapsed ? (
                    <ChevronRight className='w-5 h-5' />
                  ) : (
                    <ChevronLeft className='w-5 h-5' />
                  )}
                </button>
              </div>

              {/* ========================================
               * NAVIGATION SECTIONS
               * ======================================== */}
              <nav className='flex-1 px-3 space-y-6'>
                {navigationSections.map(section => {
                  // Verificar se usuário tem acesso à seção
                  if (section.requiresRole && !section.requiresRole.includes(userRole)) {
                    return null;
                  }

                  const isSectionExpanded = expandedSections[section.id] ?? true;

                  return (
                    <div key={section.id} className='space-y-2'>
                      {/* Section Header */}
                      {showExpanded && (
                        <div className='flex items-center justify-between px-3 mb-2'>
                          <h3 className='text-xs font-semibold text-[#64748B] dark:text-gray-400 uppercase tracking-wider'>
                            {section.title}
                          </h3>
                          {section.collapsible && (
                            <button
                              onClick={() => handleToggleSection(section.id)}
                              className='p-1 rounded hover:bg-[#F1F5F9] dark:hover:bg-gray-800
                                         focus-brand
                                         transition-colors duration-brand-fast'
                              aria-label={isSectionExpanded ? 'Recolher seção' : 'Expandir seção'}
                            >
                              {isSectionExpanded ? (
                                <ChevronRight className='w-3 h-3 rotate-90' />
                              ) : (
                                <ChevronRight className='w-3 h-3' />
                              )}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Section Items */}
                      {(isSectionExpanded || !section.collapsible) && (
                        <div className='space-y-1'>
                          {section.items.map(item => {
                            if (!hasAccess(item)) return null;

                            const isActive = isItemActive(item.path);
                            const badge = getItemBadge(item);
                            const Icon = item.icon;

                            const navItemContent = (
                              <Link
                                href={item.path}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-brand-md
                                           text-sm font-medium
                                           transition-all duration-brand-fast
                                           group
                                           ${
                                             isActive
                                               ? 'bg-brand-primary/10 text-brand-primary border-l-2 border-brand-primary'
                                               : 'text-[#111827] dark:text-gray-300 hover:bg-[#F1F5F9] dark:hover:bg-gray-800'
                                           }
                                           ${item.premium && !isActive ? 'hover:bg-purple-50 dark:hover:bg-purple-900/20' : ''}`}
                                aria-current={isActive ? 'page' : undefined}
                                data-testid={`sidebar-item-${item.id}`}
                              >
                                {/* Icon */}
                                <Icon
                                  className={`w-5 h-5 flex-shrink-0
                                             ${isActive ? 'text-brand-primary' : ''}
                                             ${item.premium && !isActive ? 'text-purple-500' : ''}`}
                                />

                                {/* Label (only when expanded) */}
                                {showExpanded && (
                                  <>
                                    <span className='flex-1 truncate'>{item.label}</span>

                                    {/* Badge or Premium Icon */}
                                    {badge !== undefined && badge !== 0 && (
                                      <span className={getBadgeClasses(item.badgeColor)}>
                                        {typeof badge === 'number' && badge > 99 ? '99+' : badge}
                                      </span>
                                    )}
                                    {item.premium && !badge && (
                                      <Crown className='w-4 h-4 text-purple-500' />
                                    )}
                                  </>
                                )}
                              </Link>
                            );

                            // Wrap com Tooltip quando colapsado
                            if (isCollapsed && !isHovering) {
                              return (
                                <Tooltip.Root key={item.id}>
                                  <Tooltip.Trigger asChild>{navItemContent}</Tooltip.Trigger>
                                  <Tooltip.Portal>
                                    <Tooltip.Content
                                      side='right'
                                      sideOffset={16}
                                      className='z-50 px-3 py-2 max-w-xs
                                                 bg-gray-900 dark:bg-gray-800
                                                 text-white text-sm
                                                 rounded-brand-md
                                                 shadow-brand-xl
                                                 animate-fadeIn'
                                    >
                                      <div className='font-medium'>{item.label}</div>
                                      {item.description && (
                                        <div className='text-xs text-[#64748B] dark:text-gray-400 mt-1'>
                                          {item.description}
                                        </div>
                                      )}
                                      <Tooltip.Arrow className='fill-gray-900 dark:fill-gray-800' />
                                    </Tooltip.Content>
                                  </Tooltip.Portal>
                                </Tooltip.Root>
                              );
                            }

                            return <div key={item.id}>{navItemContent}</div>;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>

              {/* ========================================
               * FOOTER - VERSION INFO
               * ======================================== */}
              {showExpanded && (
                <div className='px-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-800'>
                  <div className='flex items-center gap-2 px-3 py-2 text-xs text-[#64748B] dark:text-gray-400'>
                    <Zap className='w-3 h-3' />
                    <span>v3.0.0-beta</span>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea.Viewport>

          <ScrollArea.Scrollbar
            orientation='vertical'
            className='flex w-2 bg-gray-100 dark:bg-gray-800 rounded-full'
          >
            <ScrollArea.Thumb className='flex-1 bg-gray-300 dark:bg-gray-600 rounded-full' />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      </aside>
    </Tooltip.Provider>
  );
}

/**
 * Hook de atalho de teclado para toggle da sidebar
 * Uso: Ctrl+B ou Cmd+B
 */
export function useSidebarKeyboard(onToggle: () => void) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        onToggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onToggle]);
}
